import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export interface CartItem {
  productId: string
  name: string
  price: number
  qty: number
  sku?: string
}

export interface Cart {
  storeId: string
  storeName: string
  items: CartItem[]
}

interface CartContextValue {
  cart: Cart | null
  addItem: (storeId: string, storeName: string, item: Omit<CartItem, 'qty'>) => void
  removeItem: (productId: string) => void
  updateQty: (productId: string, qty: number) => void
  clearCart: () => void
  itemCount: number
  subtotal: number
}

const STORAGE_KEY = 'ferremarket_cart'

const CartContext = createContext<CartContextValue | null>(null)

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? (JSON.parse(raw) as Cart) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (cart) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [cart])

  function addItem(storeId: string, storeName: string, item: Omit<CartItem, 'qty'>) {
    setCart(prev => {
      if (prev && prev.storeId !== storeId) {
        const ok = window.confirm(
          `¿Limpiar carrito y cambiar a ${storeName}?\nPerderás los productos de "${prev.storeName}".`
        )
        if (!ok) return prev
        // Start fresh with the new store
        return {
          storeId,
          storeName,
          items: [{ ...item, qty: 1 }],
        }
      }

      if (!prev) {
        return { storeId, storeName, items: [{ ...item, qty: 1 }] }
      }

      const existing = prev.items.find(i => i.productId === item.productId)
      if (existing) {
        return {
          ...prev,
          items: prev.items.map(i =>
            i.productId === item.productId ? { ...i, qty: i.qty + 1 } : i
          ),
        }
      }

      return { ...prev, items: [...prev.items, { ...item, qty: 1 }] }
    })
  }

  function removeItem(productId: string) {
    setCart(prev => {
      if (!prev) return null
      const items = prev.items.filter(i => i.productId !== productId)
      return items.length === 0 ? null : { ...prev, items }
    })
  }

  function updateQty(productId: string, qty: number) {
    if (qty < 1) {
      removeItem(productId)
      return
    }
    setCart(prev => {
      if (!prev) return null
      return {
        ...prev,
        items: prev.items.map(i => (i.productId === productId ? { ...i, qty } : i)),
      }
    })
  }

  function clearCart() {
    setCart(null)
  }

  const itemCount = cart ? cart.items.reduce((acc, i) => acc + i.qty, 0) : 0
  const subtotal = cart ? cart.items.reduce((acc, i) => acc + i.price * i.qty, 0) : 0

  return (
    <CartContext.Provider value={{ cart, addItem, removeItem, updateQty, clearCart, itemCount, subtotal }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be inside CartProvider')
  return ctx
}
